import { useState } from 'react';

import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import { CARRIER_TYPE, type Carrier, type SortDirection } from '@klareospace/pharos-lib-types';

import { useCarriers } from './use-carriers';
import type { NationalCarrierSortableColumn } from '../types/carrier.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseNationalCarriersPageReturn {
  carriers: Carrier[];
  currentPage: number;
  error: Error | null;
  integrationFilter: boolean[];
  isLoading: boolean;
  onIntegrationChange: (values: boolean[]) => void;
  onPageChange: (newPage: number) => void;
  onSearchChange: (value: string) => void;
  onSort: (sortBy: NationalCarrierSortableColumn, sortDirection: SortDirection | null) => void;
  pageSize: number;
  searchValue: string;
  sortBy: NationalCarrierSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
  totalItems: number;
  totalPages: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useNationalCarriersPage(): UseNationalCarriersPageReturn {
  const searchState = useSearchPageState<NationalCarrierSortableColumn>();
  const [integrationFilter, setIntegrationFilter] = useState<boolean[]>([]);

  const { data, error, isLoading } = useCarriers({
    integrations: integrationFilter.length > 0 ? integrationFilter : undefined,
    page: searchState.currentPage,
    pageSize: searchState.pageSize,
    search: searchState.debouncedSearch,
    sort: searchState.sort,
    types: [CARRIER_TYPE.NATIONAL],
  });

  function handleIntegrationChange(values: boolean[]): void {
    setIntegrationFilter(values);
    searchState.resetPage();
  }

  return {
    carriers: data?.items ?? [],
    currentPage: searchState.currentPage,
    error: error ?? null,
    integrationFilter,
    isLoading,
    onIntegrationChange: handleIntegrationChange,
    onPageChange: searchState.onPageChange,
    onSearchChange: searchState.onSearchChange,
    onSort: searchState.onSort,
    pageSize: searchState.pageSize,
    searchValue: searchState.searchValue,
    sortBy: searchState.sortBy,
    sortDirection: searchState.sortDirection,
    totalItems: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
  };
}
